import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { FileText, Download, Lock, ShieldCheck, Search, X } from "lucide-react";
import { api, assetUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const CAT_LABELS = {
  plan: "Plans & Surveys",
  overview: "Overview / Land Use",
  geotechnical: "Geotechnical & Environmental",
  guidelines: "Architectural Guidelines",
  permit: "Permits",
  approval: "Approvals",
  commercial: "Commercial",
  price_list: "Price List & Inventory",
  general: "General",
};
const catLabel = (c) => CAT_LABELS[c] || (c ? c.charAt(0).toUpperCase() + c.slice(1) : "General");

const Documents = () => {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [stages, setStages] = useState([]);
  const [q, setQ] = useState("");
  const [stageF, setStageF] = useState("all");
  const [catF, setCatF] = useState("all");

  useEffect(() => {
    api.get("/documents").then((r) => setDocs(r.data)).catch(() => {});
    api.get("/stages").then((r) => setStages(r.data)).catch(() => {});
  }, [user]);

  const stageName = useMemo(() => {
    const m = {};
    stages.forEach((s) => { m[s.id] = s.name || `Stage ${s.number}`; });
    return m;
  }, [stages]);

  // docs within the currently selected stage scope (drives the type list)
  const stageScoped = useMemo(() => docs.filter((d) => {
    if (stageF === "project") return !d.stage_id;
    if (stageF !== "all") return d.stage_id === stageF;
    return true;
  }), [docs, stageF]);

  // available types narrow down to the selected stage
  const cats = useMemo(() => [...new Set(stageScoped.map((d) => d.category || "general"))].sort(), [stageScoped]);

  // if the chosen type is no longer available for the selected stage, reset to "all"
  const catFRef = useRef(catF);
  catFRef.current = catF;
  useEffect(() => {
    if (catFRef.current !== "all" && !cats.includes(catFRef.current)) setCatF("all");
  }, [cats]);

  const filtered = stageScoped.filter((d) => {
    if (catF !== "all" && (d.category || "general") !== catF) return false;
    if (q && !d.title.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const usedStageIds = [...new Set(docs.map((d) => d.stage_id).filter(Boolean))];
  const selCls = "bg-[#0C1510] border border-[#F3EEE3]/15 rounded-full text-sm text-[#F3EEE3] px-4 py-2.5 focus:outline-none focus:border-[#C9B79C] cursor-pointer";

  return (
    <div className="pt-[64px] bg-[#12201A] min-h-screen">
      <div className="max-w-[1100px] mx-auto px-5 md:px-8 py-14">
        <p className="eyebrow text-[#C9B79C] mb-3">Due Diligence</p>
        <h1 className="font-display font-light text-4xl md:text-5xl text-[#F3EEE3]">Documents</h1>
        <p className="text-[#F3EEE3]/60 mt-3 max-w-2xl">Plans, surveys, guidelines, permits and reports across every stage. Public materials are open to all; confidential documents require an investor login.</p>
        {!user && (
          <Link to="/login" className="inline-flex items-center gap-2 mt-5 text-sm text-[#C9B79C] hover:text-[#F3EEE3]">
            <Lock size={14} /> Investor login for confidential documents
          </Link>
        )}

        {/* controls */}
        <div className="mt-9 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#F3EEE3]/40" />
            <input
              data-testid="docs-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search documents…"
              className="w-full bg-[#0C1510] border border-[#F3EEE3]/15 rounded-full text-sm text-[#F3EEE3] pl-11 pr-10 py-2.5 focus:outline-none focus:border-[#C9B79C]"
            />
            {q && <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#F3EEE3]/40 hover:text-[#F3EEE3]"><X size={15} /></button>}
          </div>
          <select data-testid="docs-filter-stage" value={stageF} onChange={(e) => setStageF(e.target.value)} className={selCls}>
            <option value="all">All stages</option>
            <option value="project">Project-wide</option>
            {stages.filter((s) => usedStageIds.includes(s.id)).sort((a, b) => a.number - b.number).map((s) => (
              <option key={s.id} value={s.id}>{s.name || `Stage ${s.number}`}</option>
            ))}
          </select>
          <select data-testid="docs-filter-type" value={catF} onChange={(e) => setCatF(e.target.value)} className={selCls}>
            <option value="all">All types</option>
            {cats.map((c) => <option key={c} value={c}>{catLabel(c)}</option>)}
          </select>
          <span className="text-[#F3EEE3]/45 text-xs" data-testid="docs-count">{filtered.length} of {docs.length}</span>
        </div>

        {/* list */}
        <div className="mt-8 space-y-2.5" data-testid="docs-list">
          {filtered.map((d) => (
            <a key={d.id} href={assetUrl(`/api/documents/${d.id}/download`)} target="_blank" rel="noreferrer" data-testid={`doc-${d.id}`} className="flex items-center justify-between gap-4 rounded-xl border border-[#F3EEE3]/12 bg-[#0F1B15] px-5 py-4 hover:border-[#C9B79C]/40 transition-colors">
              <span className="flex items-center gap-3 text-[#F3EEE3] min-w-0">
                <FileText size={16} className="text-[#C9B79C] shrink-0" />
                <span className="truncate">{d.title}</span>
                {d.visibility === "confidential" && <span className="inline-flex items-center gap-1 text-[0.6rem] text-[#C79A3B] shrink-0"><ShieldCheck size={11} /> Confidential</span>}
              </span>
              <span className="flex items-center gap-3 shrink-0">
                <span className="hidden sm:inline text-[0.6rem] uppercase tracking-wide text-[#F3EEE3]/40">{d.stage_id ? stageName[d.stage_id] || "Stage" : "Project"} · {catLabel(d.category)}</span>
                <Download size={15} className="text-[#F3EEE3]/50" />
              </span>
            </a>
          ))}
          {filtered.length === 0 && <p className="text-[#F3EEE3]/40 py-6" data-testid="docs-empty">No documents match your filters.</p>}
        </div>
      </div>
    </div>
  );
};

export default Documents;
